import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { PAGE_SIZE, type PagedResult } from "../../../../components/PortfolioPerformance/pager";
import { apiGet, apiPost } from "./client";

export type PeriodStatus = "PENDING_SIGNOFF" | "APPROVED" | "SUPERSEDED";

export interface AttestationPeriod {
  periodId: string;
  periodLabel: string;
  reportName: string;
  status: PeriodStatus;
  attestedBy?: string;
  attestedAt?: string;
  note?: string;
}

// null -- skipped at the user's own request. Render an empty state, not a
// fabricated record list.
export function useAttestationPeriods() {
  return useQuery({
    queryKey: ["attestation"],
    // GET /attestation's `data` is `{ periods: [...] | null }`, not a bare
    // array -- matches app/models/attestation.py's AttestationPeriodsResponse
    // on the backend. Unwrapped here so every caller still just gets
    // AttestationPeriod[] | null.
    queryFn: async () => {
      const { periods } = await apiGet<{ periods: AttestationPeriod[] | null }>("/attestation");
      return periods;
    },
  });
}

export function useApproveAttestation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ periodId, note }: { periodId: string; note: string }) =>
      apiPost<AttestationPeriod>(`/attestation/${periodId}/approve`, { note }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["attestation"] }),
  });
}

/** The backend hands back a short-lived signed URL for the rendered report rather than
 * streaming a blob through the gateway; the mutation opens it in a new tab on success. */
export function useDownloadAttestation() {
  return useMutation({
    mutationFn: (periodId: string) => apiGet<{ url: string }>(`/attestation/${periodId}/download`),
    onSuccess: (data) => {
      if (data.url) window.open(data.url, "_blank");
    },
  });
}

// ---- APD Grid (athena_input.apd_grid): propose / approve, shown on the Attestation page ----

export interface ApdGridBand {
  /** The grid's key (e.g. "Special Mention"); several sub-categories can share one category. Absent on
   * proposals logged before sub-categories existed, which read as their category. */
  subCategory?: string;
  category: string;
  lowerLimit: number;
  upperLimit: number;
  sortOrder?: number | null;
}

export interface ApdGrid {
  bands: ApdGridBand[];
  hasPending: boolean;
}

export interface ApdGridProposal {
  id: string;
  currentGrid: ApdGridBand[];
  proposedGrid: ApdGridBand[];
  reason: string;
  proposedBy: string | null;
  proposedAt: string;
  status: "pending" | "approved" | "rejected";
  reviewedBy: string | null;
  reviewedAt: string | null;
  reviewNotes: string | null;
}

export function useApdGrid() {
  return useQuery({
    queryKey: ["apd-grid", "current"],
    queryFn: () => apiGet<ApdGrid>("/attestation/apd-grid"),
  });
}

// Server-side paged (10 rows), newest first, like every other table in this product.
export function useApdGridLog(offset: number) {
  return useQuery({
    queryKey: ["apd-grid", "log", offset],
    queryFn: () =>
      apiGet<PagedResult<ApdGridProposal>>("/attestation/apd-grid/log", {
        limit: String(PAGE_SIZE),
        offset: String(offset),
      }),
    placeholderData: keepPreviousData,
  });
}

// A proposal changes the log and the pending flag; an approval also changes the grid itself, so
// every APD-grid query is invalidated either way.
export function useProposeApdGrid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (draft: { bands: ApdGridBand[]; reason: string }) =>
      apiPost<ApdGridProposal>("/attestation/apd-grid/proposals", draft),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apd-grid"] }),
  });
}

export function useDecideApdGrid() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (args: { id: string; status: "approved" | "rejected"; notes?: string }) =>
      apiPost<ApdGridProposal>(`/attestation/apd-grid/proposals/${args.id}/decide`, {
        status: args.status,
        notes: args.notes,
      }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["apd-grid"] }),
  });
}
